import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';

/**
 * Formatting helpers.
 *
 * The backend stores and returns UTC instants. These render them in the viewer's own timezone,
 * which is the only interpretation a user can act on, while keeping the exact UTC value available
 * as a tooltip wherever precision matters (audit, evidence, timelines).
 */

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

/** Date and time in the viewer's timezone: "12 Mar 2026, 14:05". */
export function formatDateTime(value: string | null | undefined, fallback = '—'): string {
  const date = toDate(value);
  return date ? format(date, "d MMM yyyy, HH:mm") : fallback;
}

/** Date only: "12 Mar 2026". */
export function formatDate(value: string | null | undefined, fallback = '—'): string {
  const date = toDate(value);
  return date ? format(date, 'd MMM yyyy') : fallback;
}

/** "3 hours ago". Strict, so it never rounds "1 month" up from 20 days. */
export function formatRelative(value: string | null | undefined, fallback = '—'): string {
  const date = toDate(value);
  return date ? `${formatDistanceToNowStrict(date)} ago` : fallback;
}

/** The precise UTC value, for a tooltip beside a friendly one. */
export function formatUtc(value: string | null | undefined, fallback = '—'): string {
  const date = toDate(value);
  return date ? `${date.toISOString().replace('T', ' ').slice(0, 19)} UTC` : fallback;
}

/**
 * Money. The backend sends a decimal string to avoid binary floating point; it is parsed only for
 * display, never for arithmetic.
 */
export function formatMoney(
  amount: number | string | null | undefined,
  currency: string | null | undefined,
  fallback = '—',
): string {
  if (amount === null || amount === undefined || amount === '') return fallback;
  const numeric = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(numeric)) return fallback;
  try {
    return new Intl.NumberFormat(undefined, {
      style: currency ? 'currency' : 'decimal',
      currency: currency ?? undefined,
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch {
    // An unknown currency code should not blank the figure out.
    return `${numeric.toLocaleString()} ${currency ?? ''}`.trim();
  }
}

export function formatNumber(value: number | null | undefined, fallback = '—'): string {
  return value === null || value === undefined ? fallback : value.toLocaleString();
}

/** Turns SCREAMING_SNAKE_CASE into "Screaming snake case" for display. */
export function humanise(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const lower = value.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** Shortens a UUID for display, keeping enough to be recognisable. */
export function shortId(value: string | null | undefined, fallback = '—'): string {
  return value ? value.slice(0, 8) : fallback;
}

/**
 * How much warranty is left, and how alarming that is.
 *
 * Shared because two screens draw the same judgement differently - a table cell and a headline
 * tile - and the thresholds must not drift between them. Ninety days is the point at which a
 * replacement can still be budgeted for; thirty is the point at which it cannot.
 */
export type WarrantyStanding = {
  days: number;
  tone: 'neutral' | 'warning' | 'danger';
  /** "42d left", "Expires today", "Expired 9d ago". */
  label: string;
};

export function warrantyStanding(
  expiresOn: string | null | undefined,
): WarrantyStanding | null {
  if (!expiresOn) return null;

  const expiry = new Date(expiresOn).getTime();
  if (Number.isNaN(expiry)) return null;

  const days = Math.ceil((expiry - Date.now()) / (1000 * 60 * 60 * 24));
  const tone = days <= 30 ? 'danger' : days <= 90 ? 'warning' : 'neutral';
  const label =
    days < 0
      ? `Expired ${Math.abs(days)}d ago`
      : days === 0
        ? 'Expires today'
        : `${days}d left`;

  return { days, tone, label };
}
